import { IconButton, Button } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import axios from "axios";
import { toast } from "react-toastify";
import { BACKEND } from "../../config/configEnvs";
import { useMemo, useState } from "react";
import OpenCallsModal from "./handlersButtonsPanel/OpenCallsModal";
import OpenMeets from "./handlersButtonsPanel/OpenMeets";
import OpenClientReviewsRequests from "./handlersButtonsPanel/OpenClientReviewsRequests";
import DetailsButtonLarge from "../ui/components/DetailsButtonLarge";

const normalizeCategory = (category) => (category || "").toLowerCase().trim();

const isGlobalCategory = (category) => {
  const c = normalizeCategory(category);
  return c === "*" || c === "all" || c === "todas" || c === "todas las categorias";
};

const categoryMatches = (cfgCategory, currentCategory) => {
  if (isGlobalCategory(cfgCategory)) return true;
  const cfg = normalizeCategory(cfgCategory);
  const current = normalizeCategory(currentCategory);
  return (
    cfg === current || (cfg === "audiovisuales" && current === "audiovisual")
  );
};

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.includes("T") ? value.split("T")[0] : value;
  return "";
};

const parseValueForSave = (inputType, rawValue) => {
  if (inputType === "number") {
    if (rawValue === "" || rawValue === null || rawValue === undefined) return null;
    const n = Number(rawValue);
    return Number.isNaN(n) ? null : n;
  }
  return rawValue;
};

const ActionItemRow = ({ // Se maneja el renderizado de cada item del panel
  item,
  servicioData,
  setServicioData,
  onSaveField,
  actionHandlers,
}) => {
  const { title, type } = item;

  // visibility guard (item-level)
  if (typeof item.visibleIf === "function" && !item.visibleIf(servicioData)) return null;

  if (type === "button") {
    const handler = actionHandlers?.[item.handlerKey];
    const hasHandler = typeof handler === "function";
    const hideIfNoHandler = item.hideIfNoHandler !== false;
    const disableIfNoHandler = item.disableIfNoHandler !== false; // default: true
    if (!hasHandler && hideIfNoHandler) return null;

    return (
      <div className="flex flex-row w-full bg-white shadow-sm rounded-lg border border-gray-200 hover:shadow-md transition-all">
        <div className="w-[30%] flex items-center justify-start px-2 py-2 border-r border-gray-300">
          <span className="text-xs font-medium text-gray-700 break-words">
            {title}
          </span>
        </div>

        <div className="w-[70%] flex items-center justify-end px-2 py-2">
          {item.renderAs === "detailsButtonLarge" ? (
            <DetailsButtonLarge
              type={item.detailsButtonType || "eye"}
              content={item.detailsTooltip || title}
              action={() => handler?.({ servicioData, setServicioData, item })}
            />
          ) : (
            <Button
              size="small"
              variant={item.variant || "contained"}
              color={item.color || "primary"}
              startIcon={item.startIcon || undefined}
              endIcon={item.endIcon || undefined}
              fullWidth={Boolean(item.fullWidth)}
              disabled={!hasHandler && disableIfNoHandler}
              onClick={() => handler?.({ servicioData, setServicioData, item })}
            >
              {item.buttonText || "Acción"}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // input
  const field = item.field;
  const inputType = item.inputType || "text";
  const hasOptions = Array.isArray(item.options) && item.options.length > 0;

  const rawValue = servicioData?.[field];
  const viewValue =
    inputType === "date" ? toDateInputValue(rawValue) : (rawValue ?? "");
  const hasSavedValue =
    rawValue === 0
      ? true
      : rawValue
        ? (typeof rawValue === "string" ? rawValue.trim() !== "" : true)
        : false;
  const isDisabled = Boolean(item.disabled) || (item.disableIfHasValue && hasSavedValue);

  return (
    <div className="flex flex-row w-full bg-white shadow-sm rounded-lg border border-gray-200 hover:shadow-md transition-all">
      {/* Título */}
      <div className="w-[30%] flex items-center justify-start px-2 py-2 border-r border-gray-300">
        <span className="text-xs font-medium text-gray-700 break-words">
          {title}
        </span>
      </div>

      {/* Input */}
      <div className="w-[60%] flex items-center justify-start px-2 py-2 border-r border-gray-300">
        {hasOptions ? (
          <select
            value={viewValue}
            disabled={isDisabled}
            onChange={(e) => {
              const next = e.target.value;
              setServicioData((prev) => ({
                ...prev,
                [field]: next,
              }));
              if (item.saveOnChange) onSaveField(item, next);
            }}
            className={
              item.inputClassName ||
              "text-xs w-full bg-transparent py-2 outline-none text-gray-700"
            }
          >
            <option value="">Seleccionar</option>
            {item.options.map((opt) => (
              <option key={`${field}-${String(opt.value)}`} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={inputType}
            value={viewValue}
            placeholder={item.placeholder || ""}
            disabled={isDisabled}
            onChange={(e) => {
              const next =
                inputType === "number" ? e.target.value : e.target.value;
              setServicioData((prev) => ({
                ...prev,
                [field]: next,
              }));
              if (item.saveOnChange) onSaveField(item, next);
            }}
            className={
              item.inputClassName ||
              "text-xs w-full text-gray-700 py-2 outline-none border-none ring-0 focus:ring-0 focus:outline-none bg-transparent"
            }
          />
        )}
      </div>

      {/* Save */}
      <div className="w-[10%] flex justify-center items-center px-2 py-2">
        <IconButton
          size="small"
          color="success"
          onClick={() => onSaveField(item, servicioData?.[field])}
        >
          <SaveIcon fontSize="inherit" />
        </IconButton>
      </div>
    </div>
  );
};

const DEFAULT_CATEGORIES_ITEMS = [
  // Global (todas las categorías)
  {
    category: "*",
    items: [
            // {
      //   id: "global-btn-llamadas-telefonicas",
      //   type: "button",
      //   title: "Llamadas telefónicas",
      //   buttonText: "Ver / Registrar",
      //   handlerKey: "llamadasTelefonicas",
      //   hideIfNoHandler: false,
      //   disableIfNoHandler: false,
      //   color: "primary",
      //   variant: "outlined",
      // },
      {
        id: "global-btn-reuniones-cliente",
        type: "button",
        title: "Reuniones cliente",
        handlerKey: "reunionesCliente",
        hideIfNoHandler: false,
        color: "primary",
        renderAs: "detailsButtonLarge",
        detailsButtonType: "eye",
        detailsTooltip: "Ver reuniones",
      },

    ],
  },
  // Web
  {
    category: "Web",
    items: [
      {
        id: "global-date-migracion-oficial-web",
        type: "input",
        title: "Migración oficial del sitio web",
        field: "fechaMigracionOficial",
        hideIfNoHandler: false,
        inputType: "date",
        disableIfHasValue: true,
      },
      {
        id: "web-btn-solicitud-revisiones",
        type: "button",
        title: "Solicitud de revisiones al cliente",
        renderAs: "detailsButtonLarge",
        handlerKey: "solicitudRevisionesCliente",
        hideIfNoHandler: false,
        color: "secondary",
      },
      {
        id: "global-date-grabacion-tutorial",
        type: "input",
        title: "Fecha grabación tutorial",
        field: "fechaGrabacionCapacitacion",
        inputType: "date",
        disableIfHasValue: true,
      },
    ],
  },
];

const SeguimientoActionPanel = ({
  servicioData,
  setServicioData,
  config = DEFAULT_CATEGORIES_ITEMS,
  actionHandlers = {},
}) => {
  const [openCallsModal, setOpenCallsModal] = useState(false);
  const [openMeetsModal, setOpenMeetsModal] = useState(false);
  const [openClientReviewsRequestsModal, setOpenClientReviewsRequestsModal] =
    useState(false);
  const serviceId = servicioData?.id;
  const category = servicioData?.servicio?.categoria;
  const quoteId = servicioData?.cotizacion?.id;
  const clientData = servicioData?.cotizacion?.cliente;

  const handleSaveFieldDefault = async (field, value, inputType) => {
    if (!serviceId) return;
    try {
      const parsed = parseValueForSave(inputType, value);
      await axios.put(`${BACKEND}/api/serviciosCotizaciones/${serviceId}`, {
        [field]: parsed,
      });
      toast.success("Actualizado correctamente");
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const onSaveField = async (item, value) => {
    const saveHandler =
      item.saveHandlerKey && actionHandlers?.[item.saveHandlerKey];
    if (saveHandler) {
      try {
        await saveHandler({ servicioData, setServicioData, item, value });
        toast.success("Actualizado correctamente");
      } catch (e) {
        toast.error("Error al actualizar");
      }
      return;
    }

    if (!item.field) return;
    await handleSaveFieldDefault(item.field, value, item.inputType);
  };

  const mergedActionHandlers = useMemo(() => {
    return {
      ...actionHandlers,
      // default handler: abrir modal de llamadas si no viene uno desde afuera
      llamadasTelefonicas:
        actionHandlers?.llamadasTelefonicas ||
        (() => setOpenCallsModal(true)),
      // default handler: abrir modal de reuniones si no viene uno desde afuera
      reunionesCliente:
        actionHandlers?.reunionesCliente ||
        (() => setOpenMeetsModal(true)),
      // default handler: abrir modal de solicitudes de revisiones si no viene uno desde afuera
      solicitudRevisionesCliente:
        actionHandlers?.solicitudRevisionesCliente ||
        (() => setOpenClientReviewsRequestsModal(true)),
    };
  }, [actionHandlers]);

  const cfgList = config || [];
  const globalItems = cfgList
    .filter((cfg) => isGlobalCategory(cfg.category))
    .flatMap((cfg) => cfg.items || []);

  const categoryItems = cfgList
    .filter((cfg) => !isGlobalCategory(cfg.category))
    .filter((cfg) => categoryMatches(cfg.category, category))
    .flatMap((cfg) => cfg.items || []);

  const items = [...globalItems, ...categoryItems];
  if (!items.length) return null;

  return (
    <>
      <div className="flex flex-col gap-2">
        {items.map((item) => (
          <ActionItemRow
            key={item.id || `${item.type}-${item.title}`}
            item={item}
            servicioData={servicioData}
            setServicioData={setServicioData}
            onSaveField={onSaveField}
            actionHandlers={mergedActionHandlers}
          />
        ))}
      </div>

      <OpenCallsModal
        open={openCallsModal}
        onClose={() => setOpenCallsModal(false)}
        quoteId={quoteId}
        clientData={clientData}
      />

      <OpenMeets
        open={openMeetsModal}
        onClose={() => setOpenMeetsModal(false)}
        serviceId={serviceId}
      />

      <OpenClientReviewsRequests
        open={openClientReviewsRequestsModal}
        onClose={() => setOpenClientReviewsRequestsModal(false)}
        serviceId={serviceId}
      />
    </>
  );
};

export default SeguimientoActionPanel;